import { ActionMenu } from "@gladis/ui";
import { changeLanguage, resolveLanguage, useLanguage, type LanguageChoice } from "@gladis/ui/i18n";
import { LanguageIcon } from "@heroicons/react/24/outline";
import type { ReactNode } from "react";

/**
 * The language, beside the theme picker, because the two are the same kind of thing.
 *
 * ==========================================================================================
 * IT IS A MENU, AND THE THEME PICKER BESIDE IT IS A CYCLING BUTTON. THAT IS DELIBERATE.
 * ==========================================================================================
 *
 * `theme-toggle.tsx` cycles rather than opening a menu for a reason it states plainly: the rail was
 * `fixed` and 64px wide, and a popover anchored in the phone bar opened off the bottom of the
 * viewport. That constraint ended when the picker moved into the top bar on 2026-08-14, and the
 * cycling shape stayed because it was already verified and a colour scheme has three states a reader
 * can pass through harmlessly.
 *
 * A LANGUAGE CANNOT BE PASSED THROUGH HARMLESSLY. Cycling `nl -> en -> system` means a Dutch reader
 * who wants to hand the decision back to their browser must first render the entire application in
 * English — every page, every label, a language they did not ask for — for one click. A colour flash
 * is a colour flash; a language flash is the product briefly not working for the person it was built
 * for.
 *
 * The three states are also not the same KIND of thing, which a cycle implies and a menu does not:
 * two are languages and the third is a rule about how to pick one. A menu can show that; a button
 * whose label is whatever comes next cannot.
 *
 * ==========================================================================================
 * WHY IT DOES NOT CALL `setLanguageChoice`
 * ==========================================================================================
 *
 * `changeLanguage` fetches the target catalogue BEFORE switching. `setLanguageChoice` alone would
 * stamp `<html lang>`, move the money formatter and re-render every subscriber against the OLD
 * words — a half-switched application that reads as a translation gap rather than a wiring mistake.
 * `@gladis/ui/i18n` does not export the unsafe call at all, so this is the only reachable path.
 *
 * The promise is deliberately not awaited into any local state. There is no spinner and no disabled
 * interval: the catalogue is a few kilobytes on the same origin, and the first switch in each
 * direction is the only one that fetches anything.
 */

/** The order they are offered in. Dutch first: the household is Dutch, and so is the fallback. */
const CHOICES: readonly LanguageChoice[] = ["nl", "en", "system"];

/**
 * The languages are named IN THEMSELVES — "Nederlands", not "Dutch".
 *
 * A reader who cannot read the current language has to find their own in this list, and "Dutch" is
 * only findable by someone who already reads English. This is the one place in the application whose
 * labels must not follow the active language, which is why they are here rather than in the
 * catalogue: a catalogue entry would be translated, and translating this list is precisely the bug.
 */
const ENDONYM: Record<"nl" | "en", string> = {
  nl: "Nederlands",
  en: "English",
};

export function LanguageToggle(): ReactNode {
  const { choice, resolved } = useLanguage();

  /*
   * The follow-this-browser item names what the browser is currently giving, which is the fact a
   * menu of three bare items cannot state — `theme-toggle.tsx` makes the same point about `System`.
   * Without it a reader has no way to tell what following the browser would mean before choosing it.
   */
  function labelFor(item: LanguageChoice): string {
    if (item !== "system") return ENDONYM[item];
    return `${ENDONYM[resolveLanguage("system").language]} — ${resolved.language === "nl" ? "browsertaal" : "browser language"}`;
  }

  return (
    <ActionMenu
      /*
       * The accessible name carries the state as well as the purpose, because the trigger is an icon
       * and the icon is the same in every state. Written in the ACTIVE language: this control is
       * chrome, and chrome follows the reader like everything else. Only the item labels are exempt,
       * for the reason given above.
       */
      label={
        resolved.language === "nl" ? `Taal: ${labelFor(choice)}` : `Language: ${labelFor(choice)}`
      }
      items={CHOICES.map((item) => ({
        label: labelFor(item),
        // The current state is offered and disabled rather than omitted: a menu that hides the
        // active choice makes the reader infer which one they are on from what is missing.
        disabled: item === choice,
        onSelect: () => {
          void changeLanguage(item);
        },
      }))}
      trigger={
        <span
          className={
            // Matched to the theme picker and the avatar trigger beside it: same box, same radius,
            // same focus ring. The three are one group and should read as one.
            "inline-flex size-8 cursor-pointer items-center justify-center rounded-full text-muted " +
            "transition-colors outline-none hover:bg-accent hover:text-ink " +
            "focus-visible:ring-[3px] focus-visible:ring-ring/50"
          }
        >
          {/* `aria-hidden` because the trigger's own label already says everything the icon means. */}
          <LanguageIcon className="size-5" aria-hidden="true" />
        </span>
      }
    />
  );
}
